import React, { FC, useMemo } from 'react'

import clsx from 'clsx'

import { Checkbox } from '@style-port/roov-ui-components-react'
import { Txt, TableCell, LineLimitTxt } from 'src/components/atoms'
import {
  DateTxt,
  DeletingLabel,
  ProjectListTableRowLinkButtons,
  ProjectStatusTxt,
  ProjectTableRowNumberTxt,
} from 'src/components/molecules'

import { useAuthority, useProfile } from 'src/hooks'
import { ProfileEntity, SelectableProjectEntity } from 'src/types'

type Props = {
  project: SelectableProjectEntity
  profile: ProfileEntity
  rowNumber: number
  onSelectProject: (project: SelectableProjectEntity) => void
  className?: string
}

export const ProjectListTableRow: FC<Props> = ({
  project,
  profile,
  rowNumber,
  onSelectProject,
  className,
}) => {
  const { loadIsAssignProject } = useProfile()
  const { canAssignProject } = useAuthority()

  const handleOnSelect = (event: React.UIEvent<HTMLDivElement>) => {
    event.stopPropagation()
    onSelectProject(project)
  }

  const isAssignedProject = useMemo(() => {
    return loadIsAssignProject({ profile, projectId: project.id })
  }, [loadIsAssignProject, profile, project.id])

  return (
    <div className={className}>
      <div
        className={clsx(
          'tw-h-[58px] tw-bg-surface-primary tw-flex tw-items-center tw-flex-nowrap',
          !project.isDeleting && 'hover:tw-bg-surface-secondary'
        )}
      >
        {canAssignProject && (
          <TableCell width={4}>
            <div onClick={handleOnSelect} role="presentation">
              <Checkbox
                nonce={undefined}
                checked={project.isSelected}
                disabled={project.isDeleting}
                padding="--s0"
              />
            </div>
          </TableCell>
        )}
        <TableCell width={4} align="right">
          <Txt
            sizeType="s"
            className={`tw-mr-s-1 tw-text-standardText-secondary ${
              project.isDeleting ? 'tw-opacity-30' : 'tw-opacity-100'
            }`}
          >
            {rowNumber}
          </Txt>
        </TableCell>
        <TableCell width={17}>
          <LineLimitTxt
            maxLineLimit={2}
            className={`tw-text-standardText-primary tw-font-bold tw-mr-s-1 tw-ml-s-1 tw-text-left tw-break-all ${
              project.isDeleting ? 'tw-opacity-30' : 'tw-opacity-100'
            }`}
          >
            {project.name}
          </LineLimitTxt>
        </TableCell>
        <TableCell width={7}>
          {project.isDeleting ? (
            <DeletingLabel />
          ) : (
            <ProjectStatusTxt
              sizeType="s"
              status={project.status}
              className="tw-font-bold tw-mr-s-1"
            />
          )}
        </TableCell>
        <TableCell width={6}>
          <Txt
            sizeType="s"
            className={`tw-text-standardText-secondary tw-mr-s-1 ${
              project.isDeleting ? 'tw-opacity-30' : 'tw-opacity-100'
            }`}
          >
            {project.prefecture}
          </Txt>
        </TableCell>
        <TableCell width={8}>
          <LineLimitTxt
            maxLineLimit={2}
            sizeType="s"
            className={clsx(
              'tw-mr-s-1',
              profile.id === project.creator.id && 'tw-text-pattern6-3',
              project.isDeleting ? 'tw-opacity-30' : 'tw-opacity-100'
            )}
          >
            {project.creator.name}
          </LineLimitTxt>
        </TableCell>
        <TableCell width={6}>
          <DateTxt
            date={project.createdAt}
            sizeType="s"
            className={`tw-text-standardText-secondary tw-mr-s-1 ${
              project.isDeleting ? 'tw-opacity-30' : 'tw-opacity-100'
            }`}
          />
        </TableCell>
        <TableCell width={8}>
          <DateTxt
            date={project.updatedAt}
            sizeType="s"
            className={`tw-text-standardText-secondary tw-mr-s-1 ${
              project.isDeleting ? 'tw-opacity-30' : 'tw-opacity-100'
            }`}
          />
        </TableCell>
        <TableCell width={6} align="right">
          <ProjectTableRowNumberTxt
            number={project.roomCount}
            className={`tw-mr-s-1 ${project.isDeleting ? 'tw-opacity-30' : 'tw-opacity-100'}`}
          />
        </TableCell>
        <TableCell width={6} align="right">
          <ProjectTableRowNumberTxt
            number={project.purchaserCount}
            className={`tw-mr-s-1 ${project.isDeleting ? 'tw-opacity-30' : 'tw-opacity-100'}`}
          />
        </TableCell>
        <TableCell width={7} align="right">
          <ProjectTableRowNumberTxt
            number={project.applicationCount}
            className={`tw-mr-s-1 ${project.isDeleting ? 'tw-opacity-30' : 'tw-opacity-100'}`}
          />
        </TableCell>
        <TableCell className="tw-w-full">
          {isAssignedProject && (
            <div
              className={`tw-w-full tw-full-justify  tw-ml-s-1 ${
                project.isDeleting ? 'tw-opacity-30' : 'tw-opacity-100'
              }`}
            >
              <ProjectListTableRowLinkButtons project={project} iconPosition="right" size="s" />
            </div>
          )}
        </TableCell>
      </div>
    </div>
  )
}
